import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  Image,
  Box,
  useToast,
  VStack,
  Flex
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { log } from '../../utils/logger'
import { createMultipartServicesApi } from './../../utils/api-interface'
import { CategoryListResponse, CreateCategoryRequest, LocalizedName } from '../../services/network'
import axios from 'axios'
import { languages, ICategoryName, ICollapsedLangs } from '../../utils/constants'
import { handleCategoryNameChange } from '../../utils/modalActivitiesCategoriesUtils'

interface ICreateCategoryModalProps {
  isOpen: boolean
  onClose: () => void
  categories: CategoryListResponse | undefined
  setCategories: React.Dispatch<React.SetStateAction<CategoryListResponse | undefined>>
}

function CreateCategoryModal({ isOpen, onClose, setCategories }: ICreateCategoryModalProps): JSX.Element {
  const { t } = useTranslation()
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [selectedImageURL, setSelectedImageURL] = useState('')
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(true)
  const [createCategoryRequest, setCreateCategoryRequest] = useState<CreateCategoryRequest>()
  const toast = useToast()
  const [open, setOpen] = useState(isOpen)
  const [showTranslateMenu, setShowTranslateMenu] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [categoryNames, setCategoryNames] = useState<ICategoryName>(languages.reduce((acc, lang) => ({ ...acc, [lang.code]: '' }), {}))
  const [collapsedLangs, setCollapsedLangs] = useState<ICollapsedLangs>(languages.reduce((acc, lang) => ({ ...acc, [lang.code]: false }), {}))

  const handleBoxClick = (): void => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e): void => {
      const image = new window.Image()
      image.src = e.target?.result as string

      image.onload = (): void => {
        if (image.width === 36 && image.height === 36) {
          setSelectedImage(file)
          setSelectedImageURL(image.src)
        } else {
          toast({
            title: 'Invalid Image Size',
            description: 'Please upload an image of size 36x36 pixels.',
            status: 'error',
            duration: 5000,
            isClosable: true
          })
          inputRef.current!.value = ''
        }
      }
    }
    reader.readAsDataURL(file)
  }

  const handleClose = (): void => {
    onClose()
    setSelectedImageURL('')
    setSelectedImage(null)
    setCreateCategoryRequest(undefined)
    setShowTranslateMenu(false)
    setCategoryNames({})
  }

  const postNewCategory = async (): Promise<void> => {
    if (createCategoryRequest) {
      try {
        setConfirmButtonDisabled(true)
        const categoriesActivitiesApi = createMultipartServicesApi()
        const response = await categoriesActivitiesApi.createCategory(createCategoryRequest.icon, createCategoryRequest.description)
        toast({
          title: t('category_created_toast'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        setCategories((prevCategories) => {
          const updatedCategories = prevCategories ? [...prevCategories.categories] : []
          updatedCategories.push(response.data)
          return { categories: updatedCategories }
        })

        handleClose()
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response) {
            log.error('Error creating category:', error.response.data)
            toast({
              title: `${t('retry_modal_title')} ${error.response.data.message} (${error.response.data.statusCode})`,
              status: 'error',
              duration: 2000,
              isClosable: true
            })
          } else {
            log.error('No response from the server:', error.message)
          }
        }
      }
      setConfirmButtonDisabled(false)
    }
  }

  const toggleCollapseLang = (langCode: string): void => {
    setCollapsedLangs((prevState) => ({
      ...prevState,
      [langCode]: !prevState[langCode]
    }))
  }

  const handleCategoryNameChangeCallBack = useCallback(
    (langCode: string, value: string) => {
      handleCategoryNameChange(langCode, value, showTranslateMenu, categoryNames, setCategoryNames)
    },
    [categoryNames, showTranslateMenu]
  )

  useEffect(() => {
    if (selectedImage && Object.values(categoryNames).some((name) => name)) {
      //set names of not translated categories as the italian one
      setCategoryNames((prev) => {
        const italianValue = prev['it']
        const updatedCategoryNames = Object.keys(prev).reduce((acc, lang) => {
          acc[lang] = prev[lang] ? prev[lang] : italianValue
          return acc
        }, {} as ICategoryName)
        return updatedCategoryNames
      })

      setConfirmButtonDisabled(false)
      setCreateCategoryRequest({
        icon: selectedImage,
        description: categoryNames as unknown as LocalizedName
      })
    } else {
      setConfirmButtonDisabled(true)
    }
  }, [selectedImage, categoryNames])

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    eva.replace()
  }, [open, showTranslateMenu])

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered={true}>
        <ModalOverlay />
        <ModalContent marginBottom={'0px'} borderRadius={'26px 26px 0px 0px'}>
          {showTranslateMenu ? (
            <Flex alignItems='left' direction={'column'}>
              <ModalHeader>
                <Flex align='center' justify='space-between' width='100%'>
                  <Text color={'black'} fontWeight='700' fontSize='24px' whiteSpace='nowrap'>
                    {t('translate_every_lang')}
                  </Text>
                  <Button onClick={(): void => setShowTranslateMenu(false)}>
                    <Flex key={'translate'} align={'center'}>
                      <i data-eva={'globe-outline'} data-eva-fill={'#7C8A97'}></i>
                      <Text color={'greyCold500'} fontWeight='600' fontSize='12px' lineHeight='16.8px' whiteSpace='nowrap'>
                        {t('translate')}
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </ModalHeader>
              <ModalBody overflowY={'auto'} maxH={'300px'}>
                {languages.map((lang) => (
                  <Flex
                    key={lang.code}
                    alignItems='left'
                    direction={'column'}
                    bg={'rgba(143, 155, 179, 0.16)'}
                    borderRadius={'12px'}
                    padding={'16px'}
                    gap={'14px'}
                    marginBottom={'20px'}>
                    <Flex justify='space-between'>
                      <Text color={'black'} fontWeight='600' fontSize='22.4px' whiteSpace='nowrap'>
                        {t(lang.name)}
                      </Text>
                      {collapsedLangs[lang.code] ? (
                        <Button onClick={(): void => toggleCollapseLang(lang.code)}>
                          <i data-eva='minus-circle-outline' data-eva-fill={'#DD8B25'}></i>
                        </Button>
                      ) : (
                        <Button onClick={(): void => toggleCollapseLang(lang.code)}>
                          <i data-eva='plus-circle-outline' data-eva-fill={'#DD8B25'}></i>
                        </Button>
                      )}
                    </Flex>
                    {collapsedLangs[lang.code] && (
                      <div>
                        <FormControl id={`${lang.code}-name`}>
                          <FormLabel variant={'primary'}>{t('name_category')}</FormLabel>
                          <Input
                            bg='white'
                            value={categoryNames[lang.code]}
                            onChange={(e): void => handleCategoryNameChangeCallBack(lang.code, e.target.value)}
                          />
                        </FormControl>
                      </div>
                    )}
                  </Flex>
                ))}
              </ModalBody>
              <ModalFooter justifyContent='center'>
                <Button variant='outline' width={'50%'} height='50px' color={'primary.400'} borderColor={'primary.400'} onClick={handleClose} margin={'5px'}>
                  {t('cancel')}
                </Button>
                <Button variant={'primary'} width={'50%'} height='50px' onClick={(): void => setShowTranslateMenu(false)} margin={'5px'}>
                  {t('confirm')}
                </Button>
              </ModalFooter>
            </Flex>
          ) : (
            <Flex alignItems='left' direction={'column'}>
              <ModalHeader fontSize='24px' fontWeight='700'>
                <Flex align='center' justify='space-between' width='100%'>
                  <Text color={'black'} fontWeight='700' fontSize='24px' whiteSpace='nowrap'>
                    {t('create_category')}
                  </Text>
                  <Button onClick={(): void => setShowTranslateMenu(true)}>
                    <Flex key={'translate'} align={'center'}>
                      <i data-eva={'globe-outline'} data-eva-fill={'#7C8A97'}></i>
                      <Text color={'greyCold500'} fontWeight='600' fontSize='12px' lineHeight='16.8px' whiteSpace='nowrap'>
                        Traduci
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </ModalHeader>

              <ModalBody overflowY={'auto'} maxH={'300px'}>
                <HStack width={'full'} marginBottom={'4px'}>
                  <FormControl id='icon'>
                    <FormLabel variant={'primary'}>{t('icon')}</FormLabel>
                    <Box
                      alignContent={'center'}
                      width='48px'
                      height='48px'
                      borderRadius='full'
                      border='2px'
                      borderColor='greyCold100'
                      background={'greyCold100'}
                      minWidth='fit-content'
                      onClick={handleBoxClick}>
                      {selectedImage ? (
                        <Image src={selectedImageURL} alt='Selected Icon' boxSize='100%' objectFit='cover' borderRadius='full' />
                      ) : (
                        <VStack key={'logoimage'}>
                          <i data-eva={'upload-outline'} data-eva-fill={'#353F48'}></i>
                        </VStack>
                      )}
                      <Input ref={inputRef} type='file' accept='image/*' onChange={handleImageUpload} display={'none'} />
                    </Box>
                  </FormControl>
                  <Spacer></Spacer>
                  <i data-eva={'info'} data-eva-fill={'#B0BBC4'}></i>
                  <Text fontSize='12px' color='gray.500' minWidth={'fit-content'}>
                    {t('icon_info')}
                  </Text>
                </HStack>

                <FormControl id='name'>
                  <FormLabel variant={'primary'}>{t('first_name')}</FormLabel>
                  <Input bg='white' value={categoryNames['it']} onChange={(e): void => handleCategoryNameChangeCallBack('it', e.target.value)} />
                </FormControl>
              </ModalBody>

              <ModalFooter justifyContent='center'>
                <Button variant='outline' width={'50%'} height='50px' color={'primary.400'} borderColor={'primary.400'} onClick={handleClose} margin={'5px'}>
                  {t('cancel')}
                </Button>
                <Button variant={'primary'} width={'50%'} height='50px' isDisabled={confirmButtonDisabled} onClick={postNewCategory} margin={'5px'}>
                  {t('create')}
                </Button>
              </ModalFooter>
            </Flex>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

export { CreateCategoryModal }
